import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useDefinitionFeatures, useSpreadFeatures } from "../../../hooks/useDefinitionFeatures";
import { useGoogleAnalytics } from "../../../hooks/useGoogleAnalytics";
import { useHasBottomRootMenu } from "../../../hooks/useHasBottomRootMenu";
import { useLabels } from "../../../hooks/useLabels";
import { useLogicalSelectedPage, useSelectedZone } from "../../../hooks/useSelection";
import { useTemplate } from "../../../hooks/useTemplate";
import { useSetColorBackground } from "../../bottom-background-menus/background-color-panel/hooks/useSetColorBackground";
import { useSetPatternBackground } from "../../bottom-background-menus/background-pattern-panel/hooks/useSetPatternBackground";

import { useAlternativeProducts } from "../../../hooks/useAlternativeProducts";
import { useIsProductAdvancedEditMode } from "../../../hooks/useIsProductAdvancedEditMode";
import { useProductOptions } from "../../../hooks/useProductOptions";
import { useSpreadDesign } from "../../../hooks/useSpreadDesign";
import { useSupportedProductStartDate } from "../../../hooks/useSupportedProductStartDate";
import { useSelectedAlternativeProductGroupState } from "../../bottom-menu-panels/alternative-products/hooks";
import {
	BottomMenuButtons,
	IBottomMenuButton,
	IBottomMenuButtonsRef,
} from "../../bottom-menu/buttons";
import { BottomMenuIds, RootBottomMenuIds } from "../../bottom-menu/paths";
import { labels } from "./labels";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import { useSymbolsCount } from "../../../hooks/useSymbols";
import { isDeleteSymbolZoneSupported } from "../../bottom-menu-panels/symbols/menu-info";
import { BottomMenuButton } from "../../bottom-menu/bottom-menu-button";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { useMapZones } from "./use-map-zones";
import { useQrSupport } from "../../../hooks/use-qr-support";
import { QrIcon } from "../../../icons/icons";
import { showEventDialogAsync } from "../../dialogs/events-dialog";
import { MenuIds, useMenus } from "../../../hooks/useMenus";

type IRootMenuRef = IBottomMenuButtonsRef;

/**
 * Get the button to highlight in root bottom menu to make active for the current menu
 */
function useActiveRootBottomMenuId(): RootBottomMenuIds | undefined {
	const { menuId } = useMenus();

	switch (menuId) {
		// Photo
		case MenuIds.Photo:
		case MenuIds.PhotoRotate:
		case MenuIds.PhotoTransform:
		case MenuIds.PhotoBrightness:
		case MenuIds.PhotoContrast:
		case MenuIds.PhotoSaturation:
		case MenuIds.PhotoFilters:
		case MenuIds.PhotoEditing:
		case MenuIds.PhotoSelection:
		case MenuIds.PhotoZoneDepth:
			return BottomMenuIds.Photo;

		// Text
		case MenuIds.Text:
		case MenuIds.TextStyle:
		case MenuIds.TextHAlignment:
		case MenuIds.TextVAlignment:
		case MenuIds.TextColor:
		case MenuIds.TextSize:
		case MenuIds.TextZoneAdd:
		case MenuIds.TextValue:
		case MenuIds.FontFamilies:
		case MenuIds.TextZoneDepth:
			return BottomMenuIds.Text;

		case MenuIds.Layouts:
			return BottomMenuIds.Layouts;
		case MenuIds.Options:
			return BottomMenuIds.Options;

		case MenuIds.Qr:
		case MenuIds.QrAdd:
		case MenuIds.QrEdit:
			return BottomMenuIds.Qr;

		case MenuIds.Symbols:
		case MenuIds.SymbolsList:
		case MenuIds.SymbolZoneDepth:
			return BottomMenuIds.Symbols;

		case MenuIds.Designs:
			return BottomMenuIds.Designs;

		case MenuIds.ProductOptions:
		case MenuIds.AlternativeProducts:
		case MenuIds.DateSelection:
			return BottomMenuIds.Options;

		case MenuIds.Backgrounds:
		case MenuIds.BackgroundColor:
		case MenuIds.BackgroundPattern:
			return BottomMenuIds.Backgrounds;

		// No active button
		case undefined:
		case MenuIds.Root:
		case MenuIds.Map:
		case MenuIds.Events:
			return undefined;

		default:
			eXpress.utils.exhaustiveFail(menuId);
	}

	return undefined;
}

export const BottomRootMenu = React.memo(() => {
	const [
		[
			optionsTitle,
			photoMenuTitle,
			mapMenuTitle,
			textMenuTitle,
			backgroundsMenuTitle,
			templatesMenuTitle,
			designsMenuTitle,
			symbolsMenuTitle,
			eventsTitle,
		],
	] = useLabels([
		labels.options,
		labels.photo,
		labels.map,
		labels.text,
		labels.backgrounds,
		labels.templates,
		labels.designs,
		labels.symbols,
		labels.events,
	]);

	const activeButtonId = useActiveRootBottomMenuId();

	const bottomRootMenuRef = useRef<IRootMenuRef>(null);

	const { alternativeProductsGroups, isLoading: areAlternativeProductsLoading } =
		useAlternativeProducts();

	const dashboard = useDashboardContext();
	const { productOptionGroup, isLoading: areProductOptionsLoading } = useProductOptions();
	const isProductStartDateSupported = useSupportedProductStartDate();
	const hasProductAlternatives = !!alternativeProductsGroups?.length;
	const hasProductOptions = !!productOptionGroup;
	const showOptionsMenu =
		hasProductAlternatives || hasProductOptions || isProductStartDateSupported;
	const enableCalendarEvents = dashboard.canChangeCalendarEvents();

	const {
		menuId,
		actions: { setMenu },
	} = useMenus();

	const selectedZone = useSelectedZone();
	const canChangeColorBackgrounds = !!useSetColorBackground();
	const canChangePatternBackgrounds = !!useSetPatternBackground();
	const canChangeBackground = canChangeColorBackgrounds || canChangePatternBackgrounds;

	const { openZoneMapAsync, isMapsSupportedOnDefinition: showMapsButton } = useMapZones();
	const canChangeMap = !!openZoneMapAsync;

	const isAdvancedEditModeEnabled = useIsProductAdvancedEditMode();

	const selectedPage = useLogicalSelectedPage();
	const [, , , canApplyTemplate] = useTemplate();
	const canChangeTemplate = canApplyTemplate(selectedPage);

	const [, applySpreadDesign] = useSpreadDesign();
	const canChangeSpreadDesign = !!applySpreadDesign;

	// ATM no QR Edit button, only allow if QR Add or Delete is supported
	const {
		isAddSupported: isQrAddSupported,
		isDeleteSupported: isQrDeleteSupported,
		isEditSupported: isQrEditSupported,
		isAddEnabled: isQrAddEnabled,
		isEditEnabled: isQrEditEnabled,
		isDeleteEnabled: isQrDeleteEnabled,
	} = useQrSupport(selectedPage, selectedZone);

	const isQrSupported = isQrAddSupported || isQrDeleteSupported || isQrEditSupported;
	const isQrEnabled = isQrAddEnabled || isQrDeleteEnabled || isQrEditEnabled;

	const googleAnalytics = useGoogleAnalytics();
	const { createErrorHandler } = useErrorHandling();

	const symbolsCount = useSymbolsCount();
	const {
		areBackgroundsSupported,
		areBackgroundColorsSupported,
		areBackgroundPatternsSupported,
		areTemplatesSupported,
		hasFillablePhotoZonesInDefinition,
		hasFillableTextZonesInDefinition,
		areDesignsSupported,
		arePageDesignsSupported,
		areSymbolsSupported,
	} = useDefinitionFeatures();

	const { hasFillableSymbolZonesInSpread } = useSpreadFeatures();

	const hasBottomRootMenu = useHasBottomRootMenu();

	const [, setAlternativeProductGroup] = useSelectedAlternativeProductGroupState();

	const onClick = useCallback(
		(bottomMenuId: RootBottomMenuIds) => {
			const logGaEvent = (itemId: string) => {
				switch (itemId) {
					case BottomMenuIds.Photo: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootPhotosClicked",
						});
						break;
					}
					case BottomMenuIds.Map: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootMapsClicked",
						});
						break;
					}
					case BottomMenuIds.Qr: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootQrClicked",
						});
						break;
					}
					case BottomMenuIds.Text: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootTextClicked",
						});
						break;
					}
					case BottomMenuIds.Backgrounds: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootBackgroundsClicked",
						});
						break;
					}
					case BottomMenuIds.Layouts: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootLayoutsClicked",
						});
						break;
					}
					case BottomMenuIds.Designs: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootDesignsClicked",
						});
						break;
					}
					case BottomMenuIds.Symbols: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootSymbolsClicked",
						});
						break;
					}
					case BottomMenuIds.Events: {
						googleAnalytics.trackEventToGa({
							trackingAction: "EventsClicked",
						});
						break;
					}
					default:
						break;
				}
			};

			// Log Google Analytics event
			logGaEvent(bottomMenuId);

			// Open the right sub menu
			switch (bottomMenuId) {
				case BottomMenuIds.Options: {
					if (
						hasProductAlternatives &&
						alternativeProductsGroups.length === 1 &&
						!hasProductOptions &&
						!isProductStartDateSupported
					) {
						setAlternativeProductGroup(alternativeProductsGroups[0]);
						setMenu(
							menuId === MenuIds.AlternativeProducts
								? MenuIds.Root
								: MenuIds.AlternativeProducts,
						);
					} else if (
						hasProductOptions &&
						!hasProductAlternatives &&
						!isProductStartDateSupported
					) {
						setMenu(
							menuId === MenuIds.ProductOptions
								? MenuIds.Root
								: MenuIds.ProductOptions,
						);
					} else {
						setMenu(menuId === MenuIds.Options ? MenuIds.Root : MenuIds.Options);
					}
					break;
				}
				case BottomMenuIds.Photo: {
					if (menuId === MenuIds.Photo || menuId === MenuIds.PhotoSelection) {
						setMenu(MenuIds.Root);
					} else {
						setMenu(
							// Show only photo-selection panel when an empty photozone is selected
							!eXpress.core.isPhotoZone(selectedZone) ||
								(selectedZone.isEmpty &&
									selectedZone.isZoneForPhotoSourceType("photo"))
								? MenuIds.PhotoSelection
								: MenuIds.Photo,
						);
					}
					break;
				}
				case BottomMenuIds.Map: {
					setMenu(MenuIds.Map);
					openZoneMapAsync?.().catch(createErrorHandler());
					break;
				}
				case BottomMenuIds.Qr: {
					setMenu(menuId === MenuIds.Qr ? MenuIds.Root : MenuIds.Qr);
					break;
				}
				case BottomMenuIds.Text: {
					setMenu(menuId === MenuIds.Text ? MenuIds.Root : MenuIds.Text);
					break;
				}
				case BottomMenuIds.Backgrounds: {
					if (areBackgroundColorsSupported && !areBackgroundPatternsSupported) {
						setMenu(
							menuId === MenuIds.BackgroundColor
								? MenuIds.Root
								: MenuIds.BackgroundColor,
						);
					} else if (!areBackgroundColorsSupported && areBackgroundPatternsSupported) {
						setMenu(
							menuId === MenuIds.BackgroundPattern
								? MenuIds.Root
								: MenuIds.BackgroundPattern,
						);
					} else {
						setMenu(
							menuId === MenuIds.Backgrounds ? MenuIds.Root : MenuIds.Backgrounds,
						);
					}
					break;
				}
				case BottomMenuIds.Layouts: {
					setMenu(menuId === MenuIds.Layouts ? MenuIds.Root : MenuIds.Layouts);
					break;
				}
				case BottomMenuIds.Designs: {
					setMenu(menuId === MenuIds.Designs ? MenuIds.Root : MenuIds.Designs);
					break;
				}
				case BottomMenuIds.Symbols: {
					if (isAdvancedEditModeEnabled) {
						setMenu(menuId === MenuIds.Symbols ? MenuIds.Root : MenuIds.Symbols);
					} else if (isDeleteSymbolZoneSupported(selectedZone, dashboard)) {
						setMenu(menuId === MenuIds.Symbols ? MenuIds.Root : MenuIds.Symbols);
					} else {
						setMenu(
							menuId === MenuIds.SymbolsList ? MenuIds.Root : MenuIds.SymbolsList,
						);
					}
					break;
				}
				case BottomMenuIds.Events: {
					setMenu(MenuIds.Events);

					showEventDialogAsync()
						.then((result) => {
							if (!result) return;
							if (!dashboard.canChangeCalendarEvents(result)) return;

							dashboard.changeCalendarEvents(result);
						})
						.catch(createErrorHandler());
					break;
				}
				default: {
					throw new Error("No bottom menu navigation");
				}
			}
		},
		[
			googleAnalytics,
			hasProductAlternatives,
			alternativeProductsGroups,
			hasProductOptions,
			isProductStartDateSupported,
			setAlternativeProductGroup,
			setMenu,
			selectedZone,
			openZoneMapAsync,
			createErrorHandler,
			menuId,
			areBackgroundColorsSupported,
			areBackgroundPatternsSupported,
			isAdvancedEditModeEnabled,
			dashboard,
		],
	);

	const showTextButton = hasFillableTextZonesInDefinition || isAdvancedEditModeEnabled;
	const showPhotoButton = hasFillablePhotoZonesInDefinition || isAdvancedEditModeEnabled;

	const menuItems = useMemo(() => {
		const items: IBottomMenuButton<RootBottomMenuIds>[] = [];

		if (showPhotoButton) {
			items.push({
				id: BottomMenuIds.Photo,
				iconName: "photo",
				title: photoMenuTitle,
				isActive: activeButtonId === BottomMenuIds.Photo,
				onClick: () => onClick(BottomMenuIds.Photo),
			});
		}
		if (enableCalendarEvents) {
			items.push({
				id: BottomMenuIds.Events,
				iconName: "calendar",
				title: eventsTitle,
				isDisabled: !enableCalendarEvents,
				isActive: activeButtonId === BottomMenuIds.Events,
				onClick: () => onClick(BottomMenuIds.Events),
			});
		}
		if (showMapsButton) {
			items.push({
				id: BottomMenuIds.Map,
				iconName: "map",
				title: mapMenuTitle,
				isDisabled: !canChangeMap,
				isActive: activeButtonId === BottomMenuIds.Map,
				badgeText: "BETA",
				onClick: () => onClick(BottomMenuIds.Map),
			});
		}

		if (isQrSupported) {
			items.push({
				id: BottomMenuIds.Qr,
				children: <QrIcon />,
				title: "QR",
				isDisabled: !isQrEnabled,
				isActive: activeButtonId === BottomMenuIds.Qr,
				badgeText: "BETA",
				onClick: () => onClick(BottomMenuIds.Qr),
			});
		}

		if (showTextButton) {
			items.push({
				id: BottomMenuIds.Text,
				iconName: "text",
				title: textMenuTitle,
				isDisabled: !showTextButton,
				isActive: activeButtonId === BottomMenuIds.Text,
				onClick: () => onClick(BottomMenuIds.Text),
			});
		}

		if (areBackgroundsSupported) {
			items.push({
				id: BottomMenuIds.Backgrounds,
				iconName: "paint-bucket",
				title: backgroundsMenuTitle,
				isDisabled: !canChangeBackground,
				isActive: activeButtonId === BottomMenuIds.Backgrounds,
				onClick: () => onClick(BottomMenuIds.Backgrounds),
			});
		}

		if (areTemplatesSupported || arePageDesignsSupported) {
			items.push({
				id: BottomMenuIds.Layouts,
				iconName: "templates",
				title: templatesMenuTitle,
				isDisabled: !(canChangeTemplate || canChangeSpreadDesign),
				isActive: activeButtonId === BottomMenuIds.Layouts,
				onClick: () => onClick(BottomMenuIds.Layouts),
			});
		}

		if (areSymbolsSupported && !!symbolsCount) {
			items.push({
				id: BottomMenuIds.Symbols,
				iconName: "symbols",
				title: symbolsMenuTitle,
				isDisabled:
					// disabled if there are no fillable zones OR if advanced edit mode can't add symbolzones
					!(
						hasFillableSymbolZonesInSpread ||
						(selectedPage && dashboard.canAddSymbolZone(selectedPage))
					),
				isActive: activeButtonId === BottomMenuIds.Symbols,
				onClick: () => onClick(BottomMenuIds.Symbols),
			});
		}

		if (areDesignsSupported) {
			items.push({
				id: BottomMenuIds.Designs,
				iconName: "designs",
				title: designsMenuTitle,
				isDisabled: false,
				isActive: activeButtonId === BottomMenuIds.Designs,
				onClick: () => onClick(BottomMenuIds.Designs),
			});
		}

		if (showOptionsMenu) {
			items.push({
				id: BottomMenuIds.Options,
				iconName: "options",
				title: optionsTitle,
				isDisabled: !showOptionsMenu,
				isActive: activeButtonId === BottomMenuIds.Options,
				onClick: () => onClick(BottomMenuIds.Options),
			});
		}

		return items;
	}, [
		showPhotoButton,
		showMapsButton,
		isQrSupported,
		showTextButton,
		areBackgroundsSupported,
		areTemplatesSupported,
		arePageDesignsSupported,
		areSymbolsSupported,
		symbolsCount,
		areDesignsSupported,
		showOptionsMenu,
		photoMenuTitle,
		onClick,
		mapMenuTitle,
		canChangeMap,
		isQrEnabled,
		textMenuTitle,
		backgroundsMenuTitle,
		canChangeBackground,
		activeButtonId,
		templatesMenuTitle,
		canChangeTemplate,
		canChangeSpreadDesign,
		symbolsMenuTitle,
		hasFillableSymbolZonesInSpread,
		selectedPage,
		dashboard,
		designsMenuTitle,
		optionsTitle,
		eventsTitle,
		enableCalendarEvents,
	]);

	// Scroll into view active button
	const activeIndex = menuItems.findIndex((item) => item.isActive);
	useEffect(() => {
		bottomRootMenuRef.current?.scrollIntoView(activeIndex);
	}, [activeIndex]);

	if (!hasBottomRootMenu) return null;
	if (areAlternativeProductsLoading || areProductOptionsLoading) return null;

	return (
		<BottomMenuButtons
			ref={bottomRootMenuRef}
			showBackButton={false}
			fadeEnd
			scrollableClassName="bottom-root-menu"
		>
			{menuItems.map((props) => (
				<BottomMenuButton key={props.id} {...props} iconName={props.iconName} />
			))}
		</BottomMenuButtons>
	);
});
BottomRootMenu.displayName = "BottomRootMenu";
